import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, AlertTriangle, Search, X, Eye, Edit } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import Pagination from '@/components/Pagination';
import BulkActionsToolbar from '@/components/BulkActionsToolbar';
import Checkbox from '@/components/Checkbox';
import { Combobox } from '@/components/Combobox';

const Incidents = () => {
  const [incidents, setIncidents] = useState([]);
  const [chauffeurs, setChauffeurs] = useState([]);
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentIncidentId, setCurrentIncidentId] = useState(null);
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Bulk selection state
  const [selectedIncidents, setSelectedIncidents] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Filters state
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    type_incident: 'all',
    gravite: 'all',
    statut: 'all',
    chauffeur: 'all',
    dateFrom: '',
    dateTo: ''
  });
  
  const [formData, setFormData] = useState({
    operation_id: '',
    chauffeur_id: '',
    type_incident: 'Accident',
    gravite: 'Moyenne',
    description: '',
    commentaire: '',
    date_incident: new Date().toISOString().split('T')[0],
    statut: 'Ouvert'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [iResponse, cResponse, oResponse] = await Promise.all([
        api.get('/incidents'),
        api.get('/chauffeurs'),
        api.get('/operations')
      ]);
      setIncidents(iResponse.data);
      setChauffeurs(cResponse.data);
      setOperations(oResponse.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode && currentIncidentId) {
        await api.put(`/incidents/${currentIncidentId}`, formData);
        toast.success('Incident mis à jour avec succès');
      } else {
        await api.post('/incidents', formData);
        toast.success('Incident créé avec succès');
      }
      setDialogOpen(false);
      resetForm();
      setEditMode(false);
      setCurrentIncidentId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (incident) => {
    setFormData({
      operation_id: incident.operation_id || '',
      chauffeur_id: incident.chauffeur_id || '',
      type_incident: incident.type_incident || 'Accident',
      gravite: incident.gravite || 'Moyenne',
      description: incident.description || '',
      commentaire: incident.commentaire || '',
      date_incident: incident.date_incident || new Date().toISOString().split('T')[0],
      statut: incident.statut || 'Ouvert'
    });
    setCurrentIncidentId(incident.id);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleViewDetails = (incident) => {
    setSelectedIncident(incident);
    setViewDetailsOpen(true);
  };

  const resetForm = () => {
    setFormData({
      operation_id: '',
      chauffeur_id: '',
      type_incident: 'Accident',
      gravite: 'Moyenne',
      description: '',
      commentaire: '',
      date_incident: new Date().toISOString().split('T')[0],
      statut: 'Ouvert'
    });
  };

  const getChauffeurNom = (id) => {
    const c = (chauffeurs || []).find(ch => ch.id === id);
    return c ? c.nom : 'N/A';
  };

  const getOperationMatricule = (id) => {
    if (!id) return '-';
    const op = (operations || []).find(o => o.id === id);
    return op ? op.matricule || op.id.substring(0, 8) : '-';
  };

  const getClientFromOperation = (operationId) => {
    if (!operationId) return '-';
    const op = (operations || []).find(o => o.id === operationId);
    return op ? (op.client || op.client_nom || '-') : '-';
  };

  // Options formatées pour le Combobox des opérations
  // Affiche: "N° Opération - Nom Client" trié par date de création (plus récent en premier)
  const operationOptions = useMemo(() => {
    if (!operations || operations.length === 0) return [{ value: 'none', label: 'Aucune' }];
    
    // Trier par date de création (plus récent en premier)
    const sortedOps = [...operations].sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    });
    
    return [
      { value: 'none', label: 'Aucune' },
      ...sortedOps.map(op => {
        const matricule = op.matricule || op.id?.substring(0, 8) || 'N/A';
        const client = op.client || op.client_nom || 'Client inconnu';
        const dateCreation = op.created_at ? new Date(op.created_at).toLocaleDateString('fr-FR') : '';
        
        return {
          value: op.id,
          label: `${matricule} - ${client}`,
          // Texte de recherche: matricule, nom client, entreprise
          searchText: `${matricule} ${client} ${op.entreprise || ''} ${dateCreation}`.toLowerCase()
        };
      })
    ];
  }, [operations]);

  // Options formatées pour le Combobox des chauffeurs
  // Affiche: "Nom - Téléphone" avec recherche multi-critères
  const chauffeurOptions = useMemo(() => {
    if (!chauffeurs || chauffeurs.length === 0) return [];
    
    return chauffeurs.map(c => {
      const nom = c.nom || 'N/A';
      const telephone = c.telephone || 'N/A';
      const matricule = c.matricule || '';
      const societe = c.societe_transport || c.proprietaire_nom || '';
      
      return {
        value: c.id,
        label: `${nom} - ${telephone}`,
        // Texte de recherche: nom, téléphone, matricule, société
        searchText: `${nom} ${telephone} ${matricule} ${societe}`.toLowerCase()
      };
    });
  }, [chauffeurs]);

  const getGraviteBadgeColor = (gravite) => {
    switch (gravite) {
      case 'Faible': return 'bg-green-100 text-green-800';
      case 'Moyenne': return 'bg-yellow-100 text-yellow-800';
      case 'Élevée': return 'bg-orange-100 text-orange-800';
      case 'Critique': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatutBadgeColor = (statut) => {
    switch (statut) {
      case 'Ouvert': return 'bg-blue-100 text-blue-800';
      case 'En cours': return 'bg-yellow-100 text-yellow-800';
      case 'Résolu': return 'bg-green-100 text-green-800';
      case 'Fermé': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleStatusChange = async (incidentId, newStatus) => {
    try {
      await api.put(`/incidents/${incidentId}/status?statut=${newStatus}`);
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  // Filter logic with useMemo
  const filteredIncidents = useMemo(() => {
    return (incidents || []).filter(incident => {
      // Recherche texte (matricule ou description)
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchMatricule = incident.matricule?.toLowerCase().includes(searchLower);
        const matchDescription = incident.description?.toLowerCase().includes(searchLower);
        if (!matchMatricule && !matchDescription) return false;
      }

      // Filtre par type d'incident
      if (filters.type_incident !== 'all' && incident.type_incident !== filters.type_incident) {
        return false;
      }

      // Filtre par gravité
      if (filters.gravite !== 'all' && incident.gravite !== filters.gravite) {
        return false;
      }

      // Filtre par statut
      if (filters.statut !== 'all' && incident.statut !== filters.statut) {
        return false;
      }

      // Filtre par chauffeur
      if (filters.chauffeur !== 'all' && incident.chauffeur_id !== filters.chauffeur) {
        return false;
      }

      // Filtre par date (de)
      if (filters.dateFrom && incident.date_incident < filters.dateFrom) {
        return false;
      }

      // Filtre par date (à)
      if (filters.dateTo && incident.date_incident > filters.dateTo) {
        return false;
      }

      return true;
    });
  }, [incidents, filters]);

  // Pagination logic with useMemo
  const totalPages = useMemo(() => Math.ceil(filteredIncidents.length / pageSize), [filteredIncidents.length, pageSize]);
  const paginatedIncidents = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredIncidents.slice(startIndex, endIndex);
  }, [filteredIncidents, currentPage, pageSize]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      search: '',
      type_incident: 'all',
      gravite: 'all',
      statut: 'all',
      chauffeur: 'all',
      dateFrom: '',
      dateTo: ''
    });
  };

  // Bulk selection handlers
  const handleSelectAll = () => {
    const allIds = paginatedIncidents.map(i => i.id);
    setSelectedIncidents(allIds);
  };

  const handleDeselectAll = () => {
    setSelectedIncidents([]);
  };

  const handleSelectOne = (id) => {
    setSelectedIncidents(prev =>
      prev.includes(id)
        ? prev.filter(incidentId => incidentId !== id)
        : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    try {
      await api.post('/incidents/bulk-delete', { ids: selectedIncidents });
      toast.success(`${selectedIncidents.length} incident(s) supprimé(s)`);
      setSelectedIncidents([]);
      setShowDeleteDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900">Incidents</h1>
          <p className="text-gray-600 mt-2">Gestion des incidents de transport</p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="h-5 w-5 mr-2" />
          Déclarer un incident
        </Button>
      </div>

      <Card className="border-0 shadow-md">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-emerald-600" />
              Historique des incidents ({filteredIncidents.length} / {incidents.length})
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="gap-2"
            >
              <Search className="h-4 w-4" />
              {showAdvancedFilters ? 'Masquer les filtres' : 'Recherche avancée'}
            </Button>
          </div>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Recherche texte */}
                <div>
                  <Label>Recherche</Label>
                  <Input
                    placeholder="Matricule ou description..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  />
                </div>

                {/* Type d'incident */}
                <div>
                  <Label>Type d'incident</Label>
                  <Select
                    value={filters.type_incident}
                    onValueChange={(value) => setFilters({ ...filters, type_incident: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les types</SelectItem>
                      <SelectItem value="Accident">Accident</SelectItem>
                      <SelectItem value="Panne">Panne</SelectItem>
                      <SelectItem value="Retard">Retard</SelectItem>
                      <SelectItem value="Perte">Perte</SelectItem>
                      <SelectItem value="Vol">Vol</SelectItem>
                      <SelectItem value="Dommage marchandise">Dommage marchandise</SelectItem>
                      <SelectItem value="Autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Gravité */}
                <div>
                  <Label>Gravité</Label>
                  <Select
                    value={filters.gravite}
                    onValueChange={(value) => setFilters({ ...filters, gravite: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les gravités</SelectItem>
                      <SelectItem value="Faible">Faible</SelectItem>
                      <SelectItem value="Moyenne">Moyenne</SelectItem>
                      <SelectItem value="Élevée">Élevée</SelectItem>
                      <SelectItem value="Critique">Critique</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Statut */}
                <div>
                  <Label>Statut</Label>
                  <Select
                    value={filters.statut}
                    onValueChange={(value) => setFilters({ ...filters, statut: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="Ouvert">Ouvert</SelectItem>
                      <SelectItem value="En cours">En cours</SelectItem>
                      <SelectItem value="Résolu">Résolu</SelectItem>
                      <SelectItem value="Fermé">Fermé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Chauffeur */}
                <div>
                  <Label>Chauffeur</Label>
                  <Select
                    value={filters.chauffeur}
                    onValueChange={(value) => setFilters({ ...filters, chauffeur: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les chauffeurs</SelectItem>
                      {chauffeurs.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.nom} {c.prenom}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date de (from) */}
                <div>
                  <Label>Date de</Label>
                  <Input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  />
                </div>

                {/* Date à (to) */}
                <div>
                  <Label>Date à</Label>
                  <Input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  />
                </div>
              </div>

              {/* Reset button */}
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResetFilters}
                  className="gap-2"
                >
                  <X className="h-4 w-4" />
                  Réinitialiser les filtres
                </Button>
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {/* Bulk Actions Toolbar */}
          <BulkActionsToolbar
            selectedCount={selectedIncidents.length}
            totalCount={paginatedIncidents.length}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            onBulkDelete={() => setShowDeleteDialog(true)}
            entityName="incident"
          />

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="w-12 py-3 px-3">
                    <Checkbox
                      checked={selectedIncidents.length === paginatedIncidents.length && paginatedIncidents.length > 0}
                      onChange={(checked) => {
                        if (checked) {
                          handleSelectAll();
                        } else {
                          handleDeselectAll();
                        }
                      }}
                    />
                  </th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Matricule</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Date</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Opération</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Client</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Chauffeur</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Type</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Gravité</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Statut</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Description</th>
                  <th className="text-right py-3 px-3 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedIncidents.map((incident) => (
                  <tr key={incident.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-3">
                      <Checkbox
                        checked={selectedIncidents.includes(incident.id)}
                        onChange={() => handleSelectOne(incident.id)}
                      />
                    </td>
                    <td className="py-3 px-3 font-mono text-xs text-gray-600">{incident.matricule || '-'}</td>
                    <td className="py-3 px-3 text-gray-700">{new Date(incident.date_incident).toLocaleDateString('fr-FR')}</td>
                    <td className="py-3 px-3 font-mono text-xs text-gray-600">{getOperationMatricule(incident.operation_id)}</td>
                    <td className="py-3 px-3 text-gray-700">{getClientFromOperation(incident.operation_id)}</td>
                    <td className="py-3 px-3 text-gray-700">{getChauffeurNom(incident.chauffeur_id)}</td>
                    <td className="py-3 px-3 text-gray-700">{incident.type_incident}</td>
                    <td className="py-3 px-3">
                      <span className={`text-xs px-2 py-1 rounded ${getGraviteBadgeColor(incident.gravite)}`}>
                        {incident.gravite}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <Select 
                        value={incident.statut || 'Ouvert'} 
                        onValueChange={(value) => handleStatusChange(incident.id, value)}
                      >
                        <SelectTrigger className={`w-32 h-7 text-xs ${getStatutBadgeColor(incident.statut)}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Ouvert">Ouvert</SelectItem>
                          <SelectItem value="En cours">En cours</SelectItem>
                          <SelectItem value="Résolu">Résolu</SelectItem>
                          <SelectItem value="Fermé">Fermé</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-3 px-3 text-gray-700 max-w-md truncate">{incident.description}</td>
                    <td className="py-3 px-3">
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDetails(incident)}
                          title="Voir les détails"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(incident)}
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredIncidents.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                {incidents.length === 0 ? 
                  'Aucun incident déclaré' : 
                  'Aucun incident ne correspond aux filtres'
                }
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredIncidents.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={filteredIncidents.length}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) {
          setEditMode(false);
          setCurrentIncidentId(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>{editMode ? 'Modifier l\'incident' : 'Déclarer un incident'}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Date*</Label>
                <Input type="date" value={formData.date_incident} onChange={(e) => setFormData({ ...formData, date_incident: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Chauffeur*</Label>
                <Combobox
                  options={chauffeurOptions}
                  value={formData.chauffeur_id}
                  onValueChange={(value) => setFormData({ ...formData, chauffeur_id: value })}
                  placeholder="Sélectionnez un chauffeur"
                  searchPlaceholder="Rechercher par nom, téléphone, matricule..."
                  emptyText="Aucun chauffeur trouvé"
                />
              </div>
              <div className="space-y-2">
                <Label>Opération (optionnel)</Label>
                <Combobox
                  options={operationOptions}
                  value={formData.operation_id || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, operation_id: value === 'none' ? '' : value })}
                  placeholder="Sélectionnez une opération"
                  searchPlaceholder="Rechercher par N° opération, client..."
                  emptyText="Aucune opération trouvée"
                />
              </div>
              <div className="space-y-2">
                <Label>Type d'incident*</Label>
                <Select value={formData.type_incident} onValueChange={(value) => setFormData({ ...formData, type_incident: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Accident">Accident</SelectItem>
                    <SelectItem value="Panne">Panne</SelectItem>
                    <SelectItem value="Retard">Retard</SelectItem>
                    <SelectItem value="Perte marchandise">Perte marchandise</SelectItem>
                    <SelectItem value="Autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Gravité*</Label>
                <Select value={formData.gravite} onValueChange={(value) => setFormData({ ...formData, gravite: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Faible">Faible</SelectItem>
                    <SelectItem value="Moyenne">Moyenne</SelectItem>
                    <SelectItem value="Élevée">Élevée</SelectItem>
                    <SelectItem value="Critique">Critique</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Statut*</Label>
                <Select value={formData.statut} onValueChange={(value) => setFormData({ ...formData, statut: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Ouvert">Ouvert</SelectItem>
                    <SelectItem value="En cours">En cours</SelectItem>
                    <SelectItem value="Résolu">Résolu</SelectItem>
                    <SelectItem value="Fermé">Fermé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Description de l'incident*</Label>
                <Textarea 
                  value={formData.description} 
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
                  placeholder="Décrivez l'incident en détail..."
                  rows={3}
                  required
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Commentaires / Actions prises</Label>
                <Textarea 
                  value={formData.commentaire} 
                  onChange={(e) => setFormData({ ...formData, commentaire: e.target.value })} 
                  placeholder="Commentaires supplémentaires, actions prises..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">Enregistrer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={viewDetailsOpen} onOpenChange={setViewDetailsOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-emerald-600" />
              Détails de l'incident
            </DialogTitle>
          </DialogHeader>
          {selectedIncident && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-500">Matricule</Label>
                  <p className="font-mono text-sm">{selectedIncident.matricule || '-'}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">Date de l'incident</Label>
                  <p>{new Date(selectedIncident.date_incident).toLocaleDateString('fr-FR')}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">Opération liée</Label>
                  <p className="font-mono text-sm">{getOperationMatricule(selectedIncident.operation_id)}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">Client</Label>
                  <p>{getClientFromOperation(selectedIncident.operation_id)}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">Chauffeur</Label>
                  <p>{getChauffeurNom(selectedIncident.chauffeur_id)}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">Type d'incident</Label>
                  <p>{selectedIncident.type_incident}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">Gravité</Label>
                  <span className={`inline-block text-xs px-3 py-1 rounded ${getGraviteBadgeColor(selectedIncident.gravite)}`}>
                    {selectedIncident.gravite}
                  </span>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">Statut</Label>
                  <span className={`inline-block text-xs px-3 py-1 rounded ${getStatutBadgeColor(selectedIncident.statut)}`}>
                    {selectedIncident.statut}
                  </span>
                </div>
              </div>
              
              <div className="space-y-2 pt-4 border-t">
                <Label className="text-gray-500">Description de l'incident</Label>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedIncident.description || '-'}</p>
              </div>

              {selectedIncident.commentaire && (
                <div className="space-y-2 pt-4 border-t">
                  <Label className="text-gray-500">Commentaires / Actions prises</Label>
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedIncident.commentaire}</p>
                </div>
              )}

              <div className="space-y-2 pt-4 border-t text-xs text-gray-500">
                <p>Créé le: {selectedIncident.created_at ? new Date(selectedIncident.created_at).toLocaleString('fr-FR') : '-'}</p>
                {selectedIncident.updated_at && (
                  <p>Modifié le: {new Date(selectedIncident.updated_at).toLocaleString('fr-FR')}</p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDetailsOpen(false)}>Fermer</Button>
            <Button onClick={() => {
              setViewDetailsOpen(false);
              handleEdit(selectedIncident);
            }}>
              <Edit className="h-4 w-4 mr-2" />
              Modifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            Êtes-vous sûr de vouloir supprimer <span className="font-bold">{selectedIncidents.length}</span> incident(s) ?
            Cette action est irréversible.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Annuler</Button>
            <Button variant="destructive" onClick={handleBulkDelete}>Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Incidents;
